import React, { useState } from 'react';
import { X, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Select } from '../ui/select';
import { RichTextEditor } from '../ui/RichTextEditor';
import { 
  Task, 
  CreateTaskRequest, 
  UpdateTaskRequest, 
  TaskArea, 
  TaskPriority, 
  TaskStatus,
  TaskAreaLabels,
  TaskPriorityLabels,
  TaskStatusLabels
} from '../../types/task';

interface TaskFormProps {
  task?: Task | null;
  onSubmit: (data: any) => Promise<void>;
  onCancel: () => void;
  loading?: boolean;
}

export const TaskForm: React.FC<TaskFormProps> = ({ 
  task, 
  onSubmit, 
  onCancel, 
  loading = false 
}) => {
  const [formData, setFormData] = useState({
    name: task?.name || '',
    description: task?.description || '',
    area: task?.area || 'frontend' as TaskArea,
    horasEstimadas: task?.horasEstimadas || 1,
    prioridade: task?.prioridade || 'media' as TaskPriority,
    status: task?.status || 'pendente' as TaskStatus
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Nome é obrigatório';
    } else if (formData.name.length > 500) {
      newErrors.name = 'Nome deve ter no máximo 500 caracteres';
    }

    // Descrição sem limite de caracteres

    if (formData.horasEstimadas < 0.5 || formData.horasEstimadas > 200) {
      newErrors.horasEstimadas = 'Horas estimadas devem ser entre 0.5 e 200';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Erro ao salvar tarefa:', error);
    }
  };

  const handleInputChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <Card variant="glass" className="w-full max-w-2xl max-h-[90vh] overflow-auto">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl text-foreground-primary">
            {task ? 'Editar Tarefa' : 'Nova Tarefa'}
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={onCancel}
            disabled={loading}
            className="text-foreground-tertiary hover:text-foreground-primary"
          >
            <X className="w-4 h-4" />
          </Button>
        </CardHeader>

        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Nome */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-foreground-secondary">
                Nome da Tarefa *
              </Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                placeholder="Digite o nome da tarefa"
                className={errors.name ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.name && (
                <p className="text-sm text-red-400">{errors.name}</p>
              )}
            </div>

            {/* Descrição */}
            <div className="space-y-2">
              <Label htmlFor="description" className="text-foreground-secondary">
                Descrição
              </Label>
              <RichTextEditor
                id="description"
                value={formData.description}
                onChange={(value) => handleInputChange('description', value)}
                placeholder="Descreva a tarefa (opcional)"
                className={errors.description ? 'border-red-500' : ''}
                disabled={loading}
              />
              {errors.description && (
                <p className="text-sm text-red-400">{errors.description}</p>
              )}
            </div>

            {/* Grid com 2 colunas */}
            <div className="grid grid-cols-2 gap-4">
              {/* Área */}
              <div className="space-y-2">
                <Label htmlFor="area" className="text-foreground-secondary">
                  Área *
                </Label>
                <Select
                  id="area"
                  value={formData.area}
                  onChange={(value) => handleInputChange('area', value as TaskArea)}
                  options={Object.entries(TaskAreaLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                  disabled={loading}
                />
              </div>

              {/* Horas Estimadas */}
              <div className="space-y-2">
                <Label htmlFor="horasEstimadas" className="text-foreground-secondary">
                  Horas Estimadas *
                </Label>
                <Input
                  id="horasEstimadas"
                  type="number"
                  min="0.5"
                  max="200"
                  step="0.5"
                  value={formData.horasEstimadas}
                  onChange={(e) => handleInputChange('horasEstimadas', parseFloat(e.target.value))}
                  className={errors.horasEstimadas ? 'border-red-500' : ''}
                  disabled={loading}
                />
                {errors.horasEstimadas && (
                  <p className="text-sm text-red-400">{errors.horasEstimadas}</p>
                )}
              </div>
            </div>

            {/* Grid com 2 colunas */}
            <div className="grid grid-cols-2 gap-4">
              {/* Prioridade */}
              <div className="space-y-2">
                <Label htmlFor="prioridade" className="text-foreground-secondary">
                  Prioridade *
                </Label>
                <Select
                  id="prioridade"
                  value={formData.prioridade}
                  onChange={(value) => handleInputChange('prioridade', value as TaskPriority)}
                  options={Object.entries(TaskPriorityLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                  disabled={loading}
                />
              </div>

              {/* Status */}
              <div className="space-y-2">
                <Label htmlFor="status" className="text-foreground-secondary">
                  Status *
                </Label>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(value) => handleInputChange('status', value as TaskStatus)}
                  options={Object.entries(TaskStatusLabels).map(([value, label]) => ({
                    value,
                    label
                  }))}
                  disabled={loading}
                />
              </div>
            </div>

            {/* Botões */}
            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                variant="gradient"
                loading={loading}
                loadingText="Salvando..."
                className="flex-1"
              >
                <Save className="w-4 h-4 mr-2" />
                {task ? 'Atualizar' : 'Criar'} Tarefa
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={onCancel}
                disabled={loading}
              >
                Cancelar
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};